/**
 * Conversation Hooks
 *
 * Provides hooks for conversation CRUD operations using raClient.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { raClient, type RAListParams } from '@/lib/raClient'
import { api } from '@/lib/api'
import { createQueryKeys } from '@/lib/queryKeys'
import type { Conversation, ConversationListFilters } from '@/types/conversation'

// ============================================================================
// Query Keys
// ============================================================================

export const conversationKeys = createQueryKeys('conversations')

// ============================================================================
// Types
// ============================================================================

interface UseConversationsOptions {
  filters?: ConversationListFilters
  page?: number
  perPage?: number
  sort?: { field: string; order: 'ASC' | 'DESC' }
  enabled?: boolean
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch paginated list of conversations
 */
export function useConversations({
  filters = {},
  page = 1,
  perPage = 25,
  sort = { field: 'updated_at', order: 'DESC' },
  enabled = true,
}: UseConversationsOptions = {}) {
  // Build filter object for react-admin format
  const filter: Record<string, unknown> = {}
  if (filters.assistant_id?.length) {
    filter.assistant_id = filters.assistant_id
  }
  if (filters.customer_id?.length) {
    // Use nested filter format for filtering by assistant's customer_id
    // The backend's apply_filters handles nested dicts by joining the related model
    filter.assistant = { customer_id: filters.customer_id }
  }
  if (filters.channel) {
    filter.channel = filters.channel
  }
  if (filters.outcome) {
    filter.outcome = filters.outcome
  }
  if (filters.tags?.length) {
    filter.tags = filters.tags
  }
  if (filters.date_from) {
    filter.date_from = filters.date_from
  }
  if (filters.date_to) {
    filter.date_to = filters.date_to
  }
  if (filters.topic) {
    filter.topic = `%${filters.topic}%`
  }
  if (filters.summary) {
    filter.summary = `%${filters.summary}%`
  }
  if (filters.from_id) {
    filter.from_id = filters.from_id
  }

  const params: RAListParams = { page, perPage, sort, filter }

  return useQuery({
    queryKey: conversationKeys.list(params),
    queryFn: () => raClient.getList<Conversation>('conversations', params),
    enabled,
  })
}

/**
 * Hook to fetch a single conversation by ID
 */
export function useConversation(id: string | number | undefined) {
  return useQuery({
    queryKey: conversationKeys.detail(id!),
    queryFn: () => raClient.getOne<Conversation>('conversations', id!),
    enabled: !!id,
  })
}

/**
 * Hook to update a conversation
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string | number
      data: Partial<Conversation>
    }) => {
      return raClient.update<Conversation>('conversations', id, data)
    },
    onSuccess: (data, variables) => {
      // Normalize ID to string (URL params are strings, API returns numbers)
      const id = String(variables.id)
      const numericId = Number(variables.id)

      // Update the specific conversation in cache
      queryClient.setQueryData(conversationKeys.detail(id), data)

      // Also update the conversation in any cached list queries
      queryClient.setQueriesData(
        { queryKey: conversationKeys.lists() },
        (oldData: { data: Conversation[]; total: number } | undefined) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            data: oldData.data.map((conv) =>
              conv.id === numericId ? { ...conv, ...data } : conv
            ),
          }
        }
      )

      // Invalidate detail query to ensure fresh data
      queryClient.invalidateQueries({ queryKey: conversationKeys.detail(id) })
      // Invalidate list queries to refresh
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
  })
}

/**
 * Hook to delete a conversation
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string | number) => {
      await raClient.delete('conversations', id)
      return id
    },
    onSuccess: () => {
      // Invalidate all conversation queries
      queryClient.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })
}

/**
 * Hook to get specific conversations by IDs
 */
export function useConversationsByIds(ids: number[], enabled = true) {
  return useQuery({
    queryKey: conversationKeys.byIds(ids),
    queryFn: () => raClient.getMany<Conversation>('conversations', ids),
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to auto-generate a QA label for a conversation using LLM
 */
export function useAutoQAConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ conversationId }: { conversationId: string | number }) => {
      // Use longer timeout for LLM-based QA evaluation (can take 60+ seconds)
      const response = await api.post(`/conversations/${conversationId}/auto-qa`, null, {
        timeout: 120000, // 2 minutes
      })
      return response.data
    },
    onSuccess: (_, variables) => {
      const id = String(variables.conversationId)

      // Invalidate the specific conversation query
      queryClient.invalidateQueries({ queryKey: conversationKeys.detail(id) })
      // Invalidate list queries to refresh
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() })
    },
  })
}

/**
 * Violation code as returned from the API
 */
export interface ViolationCodeItem {
  code: string
  description: string
}

/**
 * Hook to fetch available violation codes for QA labeling
 * These are loaded from the backend so they can be managed in one place
 */
export function useViolationCodes() {
  return useQuery({
    queryKey: ['qa-config', 'violation-codes'],
    queryFn: async (): Promise<ViolationCodeItem[]> => {
      const response = await api.get('/conversations/qa-config/violation-codes')
      return response.data.violation_codes
    },
    staleTime: 60 * 60 * 1000, // Cache for 1 hour - these rarely change
  })
}
